import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Background, Controls, ReactFlow, applyNodeChanges } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { apiFetchJson, fetchAllPages } from "../apiClient";
import {
  CHANNEL_TYPES, NODE_KINDS, OPERATORS, REDUCERS, connect, defaultNode, emptySpec, freshNodeId, graphEdges,
  graphNodes, removeNode,
} from "../utils/workflowGraph";
import SharingPanel from "./SharingPanel";
import { useTheme } from "../contexts/ThemeContext";
import "./AdminCollections.css";
import "./WorkflowEditor.css";

const CHANNEL_NAME = /^[a-z][a-z0-9_]{0,47}$/;
const STATUS_CONFLICT = 409;

function AgentStepFields({ step, onChange, agents, channels, idPrefix }) {
  const { t } = useTranslation();
  const set = (key) => (e) => onChange({ ...step, [key]: e.target.value });
  return (
    <>
      <div className="ds-field">
        <label className="ds-label" htmlFor={`${idPrefix}-agent`}>{t("workflows.agent")}</label>
        <select id={`${idPrefix}-agent`} className="ds-select" value={step.agent_id} onChange={set("agent_id")}>
          <option value="">{t("workflows.chooseAgent")}</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <div className="ds-field">
        <label className="ds-label" htmlFor={`${idPrefix}-task`}>{t("workflows.task")}</label>
        <textarea id={`${idPrefix}-task`} className="ds-textarea" rows={3} value={step.task} onChange={set("task")} />
        <span className="ds-help">{t("workflows.taskHelp", { skipInterpolation: true })}</span>
      </div>
      <div className="ds-form-row">
        <div className="ds-field">
          <label className="ds-label" htmlFor={`${idPrefix}-output`}>{t("workflows.output")}</label>
          <select id={`${idPrefix}-output`} className="ds-select" value={step.output} onChange={set("output")}>
            {channels.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="ds-field">
          <label className="ds-label" htmlFor={`${idPrefix}-onerror`}>{t("workflows.onError")}</label>
          <select id={`${idPrefix}-onerror`} className="ds-select" value={step.on_error || "fail"} onChange={set("on_error")}>
            <option value="fail">{t("workflows.onErrorFail")}</option>
            <option value="continue">{t("workflows.onErrorContinue")}</option>
          </select>
        </div>
      </div>
    </>
  );
}

function Inspector({ spec, nodeId, setSpec, agents }) {
  const { t } = useTranslation();
  const node = spec.nodes[nodeId];
  const channels = Object.keys(spec.channels);
  const others = Object.keys(spec.nodes).filter((id) => id !== nodeId);
  if (!node) return null;
  const update = (next) => setSpec((s) => ({ ...s, nodes: { ...s.nodes, [nodeId]: next } }));
  return (
    <aside className="ds-card wf-inspector" aria-label={t("workflows.inspector")}>
      <h3 className="ac-detail-heading">{nodeId} · {t(`workflows.kind_${node.type}`)}</h3>
      {node.type === "agent" && (
        <AgentStepFields step={node} agents={agents} channels={channels} idPrefix={`wf-${nodeId}`}
                         onChange={(step) => update({ ...node, ...step })} />
      )}
      {node.type === "parallel" && (node.branches || []).map((branch, i) => (
        <fieldset key={i} className="ds-field wf-branch">
          <legend className="ds-label">{t("workflows.branch", { n: i + 1 })}</legend>
          <AgentStepFields step={branch} agents={agents} channels={channels} idPrefix={`wf-${nodeId}-${i}`}
                           onChange={(step) => update({ ...node, branches: node.branches.map((b, j) => (j === i ? step : b)) })} />
          {node.branches.length > 2 && (
            <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm"
                    onClick={() => update({ ...node, branches: node.branches.filter((_, j) => j !== i) })}>
              {t("workflows.removeBranch")}
            </button>
          )}
        </fieldset>
      ))}
      {node.type === "parallel" && (
        <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm"
                onClick={() => update({ ...node, branches: [...node.branches, defaultNode("agent")].map((b) => {
                  const { type, ...rest } = b; return rest; }) })}>
          {t("workflows.addBranch")}
        </button>
      )}
      {node.type === "router" && (
        <>
          {(node.routes || []).map((route, i) => {
            const setRoute = (patch) => update({ ...node, routes: node.routes.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
            const setWhen = (key) => (e) => setRoute({ when: { ...route.when, [key]: e.target.value } });
            return (
              <fieldset key={i} className="ds-field wf-branch">
                <legend className="ds-label">{t("workflows.route", { n: i + 1 })}</legend>
                <div className="ds-form-row">
                  <select aria-label={t("workflows.channel")} className="ds-select" value={route.when.channel} onChange={setWhen("channel")}>
                    {channels.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select aria-label={t("workflows.operator")} className="ds-select" value={route.when.op} onChange={setWhen("op")}>
                    {OPERATORS.map((op) => <option key={op} value={op}>{op}</option>)}
                  </select>
                  {!["exists", "not_exists"].includes(route.when.op) && (
                    <input aria-label={t("workflows.value")} className="ds-input" value={route.when.value ?? ""} onChange={setWhen("value")} />
                  )}
                  <select aria-label={t("workflows.goTo")} className="ds-select" value={route.to} onChange={(e) => setRoute({ to: e.target.value })}>
                    <option value="">{t("workflows.goTo")}</option>
                    {others.map((id) => <option key={id} value={id}>{id}</option>)}
                  </select>
                </div>
                <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm"
                        onClick={() => update({ ...node, routes: node.routes.filter((_, j) => j !== i) })}>
                  {t("workflows.removeRoute")}
                </button>
              </fieldset>
            );
          })}
          <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm"
                  onClick={() => update({ ...node, routes: [...(node.routes || []),
                    { when: { channel: channels[0], op: "contains", value: "" }, to: "" }] })}>
            {t("workflows.addRoute")}
          </button>
          <div className="ds-field">
            <label className="ds-label" htmlFor={`wf-${nodeId}-default`}>{t("workflows.otherwise")}</label>
            <select id={`wf-${nodeId}-default`} className="ds-select" value={node.default}
                    onChange={(e) => update({ ...node, default: e.target.value })}>
              <option value="">{t("workflows.goTo")}</option>
              {others.map((id) => <option key={id} value={id}>{id}</option>)}
            </select>
          </div>
        </>
      )}
      {node.type === "human" && (
        <>
          <div className="ds-field">
            <label className="ds-label" htmlFor={`wf-${nodeId}-prompt`}>{t("workflows.prompt")}</label>
            <textarea id={`wf-${nodeId}-prompt`} className="ds-textarea" rows={3} value={node.prompt}
                      onChange={(e) => update({ ...node, prompt: e.target.value })} />
            <span className="ds-help">{t("workflows.humanHelp")}</span>
          </div>
          <div className="ds-field">
            <label className="ds-label" htmlFor={`wf-${nodeId}-houtput`}>{t("workflows.output")}</label>
            <select id={`wf-${nodeId}-houtput`} className="ds-select" value={node.output}
                    onChange={(e) => update({ ...node, output: e.target.value })}>
              {channels.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </>
      )}
      <div className="ac-actions">
        {spec.entry !== nodeId && (
          <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm"
                  onClick={() => setSpec((s) => ({ ...s, entry: nodeId }))}>{t("workflows.makeEntry")}</button>
        )}
        <button type="button" className="ds-btn ds-btn--danger ds-btn--sm"
                onClick={() => setSpec((s) => removeNode(s, nodeId))}>{t("workflows.removeNode")}</button>
      </div>
    </aside>
  );
}

function Channels({ spec, setSpec }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const names = Object.keys(spec.channels);
  const setChannel = (channel, patch) => setSpec((s) => ({
    ...s, channels: { ...s.channels, [channel]: { ...s.channels[channel], ...patch } } }));
  const add = () => {
    if (!CHANNEL_NAME.test(name) || spec.channels[name]) return;
    setSpec((s) => ({ ...s, channels: { ...s.channels, [name]: { type: "string", reducer: "replace" } } }));
    setName("");
  };
  return (
    <section className="ds-card ac-form-stack" aria-label={t("workflows.channels")}>
      <h3 className="ac-detail-heading">{t("workflows.channels")}</h3>
      <span className="ds-help">{t("workflows.channelsHelp")}</span>
      <div className="ds-table-wrap">
        <table className="ds-table">
          <thead><tr><th>{t("workflows.channel")}</th><th>{t("workflows.type")}</th><th>{t("workflows.reducer")}</th></tr></thead>
          <tbody>
            {names.map((c) => (
              <tr key={c}>
                <td className="ds-mono">{c}</td>
                <td><select aria-label={`${c} ${t("workflows.type")}`} className="ds-select" value={spec.channels[c].type}
                            onChange={(e) => setChannel(c, { type: e.target.value })}>
                  {CHANNEL_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}</select></td>
                <td><select aria-label={`${c} ${t("workflows.reducer")}`} className="ds-select" value={spec.channels[c].reducer}
                            onChange={(e) => setChannel(c, { reducer: e.target.value })}>
                  {REDUCERS.map((x) => <option key={x} value={x}>{x}</option>)}</select></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="ds-toolbar ac-toolbar-flush">
        <input aria-label={t("workflows.newChannel")} className="ds-input" placeholder={t("workflows.newChannel")}
               value={name} onChange={(e) => setName(e.target.value)} />
        <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" onClick={add}
                disabled={!CHANNEL_NAME.test(name) || Boolean(spec.channels[name])}>{t("workflows.addChannel")}</button>
      </div>
      <div className="ds-form-row">
        {[["input_channel", "inputChannel"], ["output_channel", "outputChannel"]].map(([key, label]) => (
          <div className="ds-field" key={key}>
            <label className="ds-label" htmlFor={`wf-${key}`}>{t(`workflows.${label}`)}</label>
            <select id={`wf-${key}`} className="ds-select" value={spec[key]}
                    onChange={(e) => setSpec((s) => ({ ...s, [key]: e.target.value }))}>
              {names.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        ))}
        <div className="ds-field">
          <label className="ds-label" htmlFor="wf-visits">{t("workflows.maxVisits")}</label>
          <input id="wf-visits" type="number" min={1} className="ds-input ds-input--sm" value={spec.max_node_visits}
                 onChange={(e) => setSpec((s) => ({ ...s, max_node_visits: Number(e.target.value) || 1 }))} />
        </div>
      </div>
    </section>
  );
}

/** Build, validate, save and run a workflow: a canvas of agent/parallel/router/human/end nodes,
 * the shared channels they read and write, and an inspector for the selected node. */
export default function WorkflowEditor({ database, workflow, onDone }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [meta, setMeta] = useState({
    name: workflow?.name || "", description: workflow?.description || "",
    max_steps: workflow?.max_steps || 20, max_seconds: workflow?.max_seconds || 600,
    visibility: workflow?.visibility || "private" });
  const [spec, setSpec] = useState(() => ({ ...emptySpec(), ...(workflow?.spec || {}) }));
  const [positions, setPositions] = useState(() => workflow?.spec?.layout || {});
  const [version, setVersion] = useState(workflow?.version ?? null);
  const [workflowId, setWorkflowId] = useState(workflow?.id || null);
  const [selected, setSelected] = useState(null);
  const [agents, setAgents] = useState([]);
  const [message, setMessage] = useState(null);       // {tone, text}
  const [task, setTask] = useState("");
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    fetchAllPages("/api/v1/agents?scope=all")
      .then((items) => { if (mounted.current) setAgents(items.filter((a) => a.can_run)); })
      .catch((e) => { if (mounted.current) setMessage({ tone: "danger", text: e.message }); });
    return () => { mounted.current = false; };
  }, []);

  const agentNames = useMemo(() => Object.fromEntries(agents.map((a) => [a.id, a.name])), [agents]);
  const nodes = useMemo(() => graphNodes(spec, positions, agentNames).map((n) => ({ ...n, selected: n.id === selected })),
    [spec, positions, agentNames, selected]);
  const edges = useMemo(() => graphEdges(spec), [spec]);

  const onNodesChange = useCallback((changes) => {
    const moved = applyNodeChanges(changes.filter((c) => c.type === "position" && c.position), nodes);
    setPositions((p) => ({ ...p, ...Object.fromEntries(moved.map((n) => [n.id, n.position])) }));
  }, [nodes]);

  const addNode = (kind) => {
    const id = freshNodeId(spec, kind);
    setSpec((s) => ({ ...s, entry: s.entry || id, nodes: { ...s.nodes, [id]: defaultNode(kind) } }));
    setSelected(id);
  };

  const body = () => ({ ...meta, spec: { ...spec, layout: positions } });
  const report = (err) => {
    const detail = err.body && err.body.detail;
    const text = detail && detail.field ? `${detail.field}: ${detail.message || (detail.invalid || []).join(", ")}`
      : (detail && detail.message) || err.message;
    setMessage({ tone: "danger", text });
  };

  const validate = async () => {
    try {
      await apiFetchJson("/api/v1/workflows/validate", { method: "POST", body: JSON.stringify({ spec: body().spec }) });
      setMessage({ tone: "success", text: t("workflows.valid") });
    } catch (err) {
      report(err);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const saved = workflowId
        ? await apiFetchJson(`/api/v1/workflows/${workflowId}`, {
          method: "PATCH", headers: { "If-Match": String(version) }, body: JSON.stringify(body()) })
        : await apiFetchJson(`/api/v1/workflows${database ? `?database=${encodeURIComponent(database)}` : ""}`, {
          method: "POST", body: JSON.stringify(body()) });
      setWorkflowId(saved.id);
      setVersion(saved.version);
      setMessage({ tone: "success", text: t("workflows.saved", { version: saved.version }) });
    } catch (err) {
      if (err.status === STATUS_CONFLICT) setMessage({ tone: "warning", text: t("workflows.conflict") });
      else report(err);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const run = async () => {
    if (!workflowId || !task.trim()) return;
    setBusy(true);
    try {
      const started = await apiFetchJson(`/api/v1/workflows/${workflowId}/runs`, {
        method: "POST", body: JSON.stringify({ task }) });
      setMessage({ tone: "success", text: t("workflows.started", { id: started.id }) });
      setTask("");
    } catch (err) {
      report(err);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <div className="ds-page ac-form-stack">
      <header className="ds-page-header">
        <h2 className="ds-page-title">{t(workflowId ? "workflows.editTitle" : "workflows.createTitle")}</h2>
      </header>
      {message && <div className={`ds-alert ds-alert--${message.tone}`} role="alert">{message.text}</div>}
      <div className="ds-form-row">
        <div className="ds-field">
          <label className="ds-label" htmlFor="wf-name">{t("workflows.name")}</label>
          <input id="wf-name" className="ds-input" value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} />
        </div>
        <div className="ds-field">
          <label className="ds-label" htmlFor="wf-steps">{t("workflows.maxSteps")}</label>
          <input id="wf-steps" type="number" min={1} className="ds-input ds-input--sm" value={meta.max_steps}
                 onChange={(e) => setMeta({ ...meta, max_steps: Number(e.target.value) || 1 })} />
        </div>
        <div className="ds-field">
          <label className="ds-label" htmlFor="wf-seconds">{t("workflows.maxSeconds")}</label>
          <input id="wf-seconds" type="number" min={1} className="ds-input ds-input--sm" value={meta.max_seconds}
                 onChange={(e) => setMeta({ ...meta, max_seconds: Number(e.target.value) || 1 })} />
        </div>
        <div className="ds-field">
          <label className="ds-label" htmlFor="wf-visibility">{t("agents.fieldVisibility")}</label>
          <select id="wf-visibility" className="ds-select" value={meta.visibility}
                  onChange={(e) => setMeta({ ...meta, visibility: e.target.value })}>
            <option value="private">{t("agents.visibilityPrivate")}</option>
            <option value="workspace">{t("agents.visibilityWorkspace")}</option>
          </select>
        </div>
      </div>
      <div className="ds-toolbar" role="toolbar" aria-label={t("workflows.palette")}>
        {NODE_KINDS.map((kind) => (
          <button key={kind} type="button" className="ds-btn ds-btn--secondary ds-btn--sm" onClick={() => addNode(kind)}>
            + {t(`workflows.kind_${kind}`)}
          </button>
        ))}
      </div>
      <div className="wf-workspace">
        <div className="wf-canvas" data-testid="workflow-canvas">
          <ReactFlow nodes={nodes} edges={edges} fitView colorMode={theme}
                     onNodesChange={onNodesChange}
                     onConnect={(c) => setSpec((s) => connect(s, c.source, c.target))}
                     onNodeClick={(_, node) => setSelected(node.id)}
                     onPaneClick={() => setSelected(null)}>
            <Background />
            <Controls />
          </ReactFlow>
        </div>
        {selected && <Inspector spec={spec} nodeId={selected} setSpec={setSpec} agents={agents} />}
      </div>
      <Channels spec={spec} setSpec={setSpec} />
      <div className="ac-actions">
        <button type="button" className="ds-btn ds-btn--secondary" onClick={onDone}>{t("agents.back")}</button>
        <button type="button" className="ds-btn ds-btn--secondary" onClick={validate}>{t("workflows.validate")}</button>
        <button type="button" className="ds-btn ds-btn--primary" disabled={busy || !meta.name.trim()} onClick={save}>
          {t("agents.save")}
        </button>
      </div>
      {workflowId && (
        <section className="ds-card ac-form-stack" aria-label={t("workflows.run")}>
          <label className="ds-label" htmlFor="wf-task">{t("workflows.runTitle")}</label>
          <textarea id="wf-task" className="ds-textarea" rows={2} value={task} onChange={(e) => setTask(e.target.value)} />
          <div className="ac-actions">
            <button type="button" className="ds-btn ds-btn--primary" disabled={busy || !task.trim()} onClick={run}>
              {t("workflows.run")}
            </button>
          </div>
        </section>
      )}
      {workflowId && workflow?.can_manage && <SharingPanel accessPath={`/api/v1/workflows/${workflowId}/access`} />}
    </div>
  );
}
